import { type UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { type CreatePointsGroupFormSchemaValues } from 'src/features/parkingManagement/api/PointsGroup/useCreatePointsGroup';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { useListPoints } from 'src/features/points/api/useListPoints.ts';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect.tsx';

type PointsGroupFormProps = {
    form: UseFormReturn<CreatePointsGroupFormSchemaValues>;
    onSubmit: (values: CreatePointsGroupFormSchemaValues) => void;
};

export default function PointsGroupForm({ onSubmit, form }: PointsGroupFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data: pointsData } = useListPoints({ parkingId: parkingId ?? '', config: { enabled: !!parkingId } });
    const allPoints = pointsData?.results ?? [];

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(parkingManagementMessages.pointsGroupName)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder={formatMessage(parkingManagementMessages.pointsGroupName)}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="points"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;

                    const fieldData = field.value
                        ?.map((groupPoint) => allPoints?.find((p) => p.id === groupPoint)?.name)
                        .join(', ');

                    const allValues = allPoints.map((point) => point.id);

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...parkingManagementMessages.pointsGroupPoints} />
                                <span className="text-blue-800">*</span>
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="w-full">
                                                {fieldData.length > 0 ? (
                                                    fieldData
                                                ) : (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...parkingManagementMessages.pointsGroupPoints}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {allPoints?.map((point) => {
                                                return (
                                                    <MultiSelectOption key={point.id} selected={point.id}>
                                                        {point.name}
                                                    </MultiSelectOption>
                                                );
                                            })}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}
